import React, { createContext, useContext, useState } from 'react';
import { Snackbar, Box, Typography, IconButton } from '@mui/material';
import { ChatBubbleXmark, CheckCircle, WarningCircle, InfoCircle, Xmark } from 'iconoir-react';

export const TOAST_TYPES = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
} as const;

export type ToastType = typeof TOAST_TYPES[keyof typeof TOAST_TYPES];

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const TOAST_CONFIG: Record<ToastType, { color: string; borderColor: string; bg: string; Icon: React.FC<any> }> = {
  error:   { color: '#E20F0F', borderColor: '#E6E8EC', bg: '#FFFFFF', Icon: ChatBubbleXmark },
  warning: { color: '#E67E22', borderColor: '#E6E8EC', bg: '#FFFFFF', Icon: WarningCircle },
  success: { color: '#1E9E5E', borderColor: '#E6E8EC', bg: '#FFFFFF', Icon: CheckCircle },
  info:    { color: '#1A6FB5', borderColor: '#E6E8EC', bg: '#FFFFFF', Icon: InfoCircle },
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState<ToastType>('error');

  const showToast = (message: string, type: ToastType = 'error') => {
    setMessage(message);
    setType(type);
    setOpen(true);
  };

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const { color, borderColor, bg, Icon } = TOAST_CONFIG[type];

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 16, sm: 24 } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2.5,
            py: 1.25,
            borderRadius: '999px',
            border: `1.5px solid ${borderColor}`,
            backgroundColor: bg,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            minWidth: 260,
            maxWidth: 480,
            direction: 'rtl',
          }}
        >
          <Icon
            width={20}
            height={20}
            color={color}
            strokeWidth={1.8}
            style={{ flexShrink: 0 }}
          />
          <Typography
            sx={{
              flex: 1,
              fontSize: '0.9rem',
              fontWeight: 500,
              color,
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ p: 0.25, color, flexShrink: 0, '&:hover': { bgcolor: 'transparent', opacity: 0.7 } }}
          >
            <Xmark width={16} height={16} strokeWidth={2} />
          </IconButton>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  );
};
